// ─── Grammaire d'accès UNIFIÉE (Core) ────────────────────────────
// Un seul modèle pour toutes les ressources de la suite (calendriers,
// inventaires, plans, finance…) :
//   acces: { [uid | '*'] : 'lecteur' | 'contributeur' | 'gestionnaire' }
//     '*'          = « Tout l'espace » (tout membre hérite du niveau)
//     [uid]        = accès nominatif (peut ÉLEVER au-dessus de '*')
//   accesUids      = miroir des uids nominatifs (≥ lecteur) pour array-contains
//   accesTous      = booléen : '*' présent (les membres voient tous)
// Le PROPRIÉTAIRE (creePar) est gestionnaire implicite, jamais stocké, jamais
// verrouillable dehors. Absence totale d'accès = privé (seul le propriétaire).
//
// 3 niveaux ordonnés : lecteur < contributeur < gestionnaire.
//   lecteur       = consulte
//   contributeur  = + modifie le CONTENU (événements / articles+quantités / rangements)
//   gestionnaire  = + configuration + gestion de la liste d'accès

export const NIVEAUX = ['lecteur', 'contributeur', 'gestionnaire'];
const RANG = { lecteur: 1, contributeur: 2, gestionnaire: 3 };
export const rangNiveau = (n) => RANG[n] || 0;
const niveauDeRang = (r) => (r >= 3 ? 'gestionnaire' : r === 2 ? 'contributeur' : r === 1 ? 'lecteur' : null);

// Niveau EFFECTIF d'un uid sur une ressource : max( '*', nominatif ) ; le
// propriétaire est toujours gestionnaire. null = aucun accès.
export function niveauDe(acces, uid, ownerUid) {
  if (uid && ownerUid && uid === ownerUid) return 'gestionnaire';
  const a = acces || {};
  return niveauDeRang(Math.max(rangNiveau(a[uid]), rangNiveau(a['*'])));
}

export const peutVoir    = (acces, uid, ownerUid) => rangNiveau(niveauDe(acces, uid, ownerUid)) >= 1;
export const peutEcrire  = (acces, uid, ownerUid) => rangNiveau(niveauDe(acces, uid, ownerUid)) >= 2;
export const peutGerer   = (acces, uid, ownerUid) => rangNiveau(niveauDe(acces, uid, ownerUid)) >= 3;

// Définit (ou retire si `niveau` falsy) l'accès d'une clé (uid ou '*').
// Renvoie un NOUVEL objet acces (immuable).
export function definirNiveau(acces, cle, niveau) {
  const a = { ...(acces || {}) };
  if (!niveau) delete a[cle]; else a[cle] = niveau;
  return a;
}

// Miroir requêtable : uids NOMINATIFS ayant au moins « lecteur » (hors '*').
export function accesUidsDe(acces) {
  const a = acces || {};
  return Object.keys(a).filter(k => k !== '*' && rangNiveau(a[k]) >= 1);
}
// '*' présent → tout l'espace voit (les règles s'appuient dessus).
export const accesTousDe = (acces) => rangNiveau((acces || {})['*']) >= 1;

// Champs à écrire en base à côté de `acces` (miroirs pour requêtes/règles).
export function miroirsAcces(acces) {
  return { accesUids: accesUidsDe(acces), accesTous: accesTousDe(acces) };
}

// ── Rétro-compat : convertit d'anciens formats vers `acces` unifié ──
// Calendrier : { [uid]: { voir, ecrire, config } } → niveau.
export function accesDepuisMatrice(matrice) {
  const out = {};
  for (const [uid, d] of Object.entries(matrice || {})) {
    if (!d) continue;
    if (d.config) out[uid] = 'gestionnaire';
    else if (d.ecrire) out[uid] = 'contributeur';
    else if (d.voir) out[uid] = 'lecteur';
  }
  return out;
}
// Inventaire : lecteurs[] + redacteurs[] → niveau (redacteur = contributeur).
export function accesDepuisListes(lecteurs, redacteurs) {
  const out = {};
  (lecteurs || []).forEach(uid => { out[uid] = 'lecteur'; });
  (redacteurs || []).forEach(uid => { out[uid] = 'contributeur'; });
  return out;
}
// Plans/finance : lecteurs (null = tout le monde) → acces. null → { '*': niveau }.
export function accesDepuisLecteurs(lecteurs, niveauTous = 'lecteur') {
  if (!Array.isArray(lecteurs)) return { '*': niveauTous };   // absent = ouvert
  const out = {};
  lecteurs.forEach(uid => { out[uid] = 'lecteur'; });
  return out;
}
